/**
 * Contrôles de saisie — guide v1.2 §4.5.
 *
 * La normalisation téléphone / email n'est pas cosmétique : ces valeurs
 * sont indexées par empreinte sous contrainte d'unicité. Deux écritures
 * du même contact créeraient deux identités distinctes.
 */

/** Borne `pageSize` du guide (§4.5 / annexe D). */
export const MAX_PAGE_SIZE = 200;
export const MIN_PAGE = 1;

/** MIME admis pour les pièces KYC. */
export const KYC_ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
] as const;

export type KycAllowedMimeType = (typeof KYC_ALLOWED_MIME_TYPES)[number];

/** Attribut `accept` des zones d'upload pièces KYC. */
export const KYC_FILE_ACCEPT = KYC_ALLOWED_MIME_TYPES.join(",");

const E164_RE = /^\+[1-9]\d{7,14}$/;
const SHA256_RE = /^[a-fA-F0-9]{64}$/;
const IDEMPOTENCY_KEY_RE = /^[A-Za-z0-9_:-]{1,100}$/;

/** Compacte un identifiant : majuscules, espaces et tirets retirés. */
export const compactIdentifier = (value: string): string =>
  value.replace(/[\s-]+/g, "").toUpperCase();

/**
 * Normalise un téléphone en E.164.
 * `+224620000000`, `00224620000000`, `620000000` et `620 00 00 00`
 * désignent le même abonné (indicatif GN par défaut pour un national 8–9).
 */
export const normalizePhone = (
  raw: string,
  defaultCountryCode = "224"
): string => {
  const trimmed = raw.trim();
  if (!trimmed) return "";

  const digits = trimmed.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) {
    return `+${digits.slice(1).replace(/\D/g, "")}`;
  }
  if (digits.startsWith("00")) {
    return `+${digits.slice(2)}`;
  }
  if (digits.length === 8 || digits.length === 9) {
    return `+${defaultCountryCode}${digits}`;
  }
  return digits.startsWith("+") ? digits : `+${digits}`;
};

/** Téléphone E.164 (8 à 15 chiffres après le +). Chaîne vide = OK (optionnel). */
export const isValidPhoneE164 = (value: string): boolean => {
  if (!value) return true;
  return E164_RE.test(value);
};

/**
 * Email minuscule, un seul `@`, domaine routable (pas `localhost`),
 * partie locale ≤ 64, adresse ≤ 254.
 */
export const normalizeEmail = (raw: string): string => raw.trim().toLowerCase();

export const isValidRoutableEmail = (value: string): boolean => {
  if (!value) return true;
  if (value.length > 254) return false;
  const at = value.indexOf("@");
  if (at <= 0 || at !== value.lastIndexOf("@")) return false;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (local.length > 64 || !local || !domain) return false;
  if (domain === "localhost" || !domain.includes(".")) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

export const clampPageSize = (pageSize: number): number =>
  Math.min(MAX_PAGE_SIZE, Math.max(MIN_PAGE, Math.trunc(pageSize) || MIN_PAGE));

export const clampPage = (page: number): number =>
  Math.max(MIN_PAGE, Math.trunc(page) || MIN_PAGE);

export const isValidKycMimeType = (mimeType: string): boolean =>
  (KYC_ALLOWED_MIME_TYPES as readonly string[]).includes(mimeType);

/** `storageKey` : ni `..` ni barre oblique initiale. */
export const isValidStorageKey = (storageKey: string): boolean =>
  Boolean(storageKey) &&
  !storageKey.startsWith("/") &&
  !storageKey.includes("..");

export const isValidChecksumSha256 = (hex: string): boolean => SHA256_RE.test(hex);

export const isValidIdempotencyKey = (key: string): boolean =>
  IDEMPOTENCY_KEY_RE.test(key);
